// AI Tools Database: Home Page
// Design: Warm Minimalism, calm, intelligent, approachable
// Layout: Hero with role anchors → sticky filter bar → tool card grid

import { useState, useMemo, useEffect, useRef } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { Link } from "wouter";
import { tools as allTools, isPublished, ROLES, ALL_ROLES, PRICING_TYPES, SKILL_LEVELS, type Role, type PricingType, type SkillLevel } from "@/lib/toolsData";

// Unpublished entries never reach the grid or the counts. See Tool.isPublished.
const tools = allTools.filter(isPublished);
import ProblemFinder from "@/components/ProblemFinder";
import SaveToolButton from "@/components/SaveToolButton";
import RoleContextCard from "@/components/RoleContextCard";
import ToolLogo from "@/components/ToolLogo";
import PricingBadge from "@/components/PricingBadge";
import { useAuth } from "@/core/hooks/useAuth";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { ROLE_CARD_STYLES, ROLE_ICONS, ROLE_LABELS } from "@/lib/roleDisplay";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
const HERO_BG = "/assets/hero-bg-Y8F6k5ayA9btVQcqwjUfpV.webp";

const ROLE_BORDER_STYLES: Record<Role, string> = {
  Atlas: "border-l-4 border-l-[oklch(0.32_0.08_255)]",
  Comet: "border-l-4 border-l-[oklch(0.55_0.12_70)]",
  Manus: "border-l-4 border-l-[oklch(0.38_0.09_155)]",
};

const ROLE_BADGE_STYLES: Record<Role, string> = {
  Atlas: "bg-[oklch(0.32_0.08_255/0.10)] text-[oklch(0.32_0.08_255)] border border-[oklch(0.32_0.08_255/0.25)]",
  Comet: "bg-[oklch(0.55_0.12_70/0.10)] text-[oklch(0.55_0.12_70)] border border-[oklch(0.55_0.12_70/0.25)]",
  Manus: "bg-[oklch(0.38_0.09_155/0.10)] text-[oklch(0.38_0.09_155)] border border-[oklch(0.38_0.09_155/0.25)]",
};

/**
 * Free / Paid are deliberately colourless.
 *
 * They were emerald and blue, filled, sitting directly beside the green
 * "Create" and navy "Think" chips. Four colours on one card, only two of which
 * meant anything, so the colour taught the reader nothing and the taxonomy
 * lost the fight for attention. Colour now belongs to Think / Clarify /
 * Create alone; price is information, not a category.
 */
// ── HERO SECTION COMPONENT ──────────────────────────────────────────────────
function HeroSection({
  setActiveRoles,
}: {
  setActiveRoles: (r: Role[]) => void;
}) {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const mobileVideoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const players: any[] = [];
    if (videoContainerRef.current) {
      players.push({ float: false, autoplay: false, allowPlaylistAds: false, target: videoContainerRef.current, videoID: "UoiMMQekY7u" });
    }
    if (mobileVideoContainerRef.current) {
      players.push({ float: false, autoplay: false, allowPlaylistAds: false, target: mobileVideoContainerRef.current, videoID: "UoiMMQekY7u" });
    }
    (window as any).openVideoPlayers = (window as any).openVideoPlayers || [];
    players.forEach(p => (window as any).openVideoPlayers.push(p));
    if (!document.querySelector('script[src="https://open.video/video.js"]')) {
      const script = document.createElement("script");
      script.src = "https://open.video/video.js";
      script.async = true;
      script.setAttribute("data-cfasync", "false");
      document.body.appendChild(script);
    } else {
      if ((window as any).openVideo && typeof (window as any).openVideo.init === 'function') {
        (window as any).openVideo.init();
      }
    }
  }, []);

  return (
    <section className="relative overflow-hidden">
      {/* The watercolour photograph used to sit here at 55% opacity. It is
          mottled, so text contrast varied across the block, worst exactly
          where the role cards sit. Replaced with a flat cream wash in the same
          palette plus the site's own dot grid, which keeps the texture without
          fighting the copy.

          HERO_BG is deliberately still referenced below and the asset must
          stay: it is one of the two files rescued from the Manus CDN and it is
          the og:image for every social preview. It now provides a faint tint
          rather than a background. */}
      <div className="absolute inset-0 bg-[oklch(0.97_0.012_75)]" />
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.10]"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 dot-grid opacity-60" />
      <div className="relative container py-6 md:py-9">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-stretch">
          <div className="w-full lg:w-1/2 flex flex-col">
            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1.5">
                The AI Roles Map
              </p>
              <h1 className="font-['Fraunces'] text-3xl md:text-4xl font-bold text-foreground leading-tight mb-3">
                The right tool starts with<br />
                <em className="not-italic text-primary">the right question.</em>
              </h1>
              {/* Says what the Roles Map is and how long it takes. Without
                  this, the three cards below read as an explainer and nobody
                  discovers they are a quiz until they click one. */}
              <p className="text-base text-foreground/70 leading-relaxed max-w-lg">
                Every AI tool plays a role. The AI Roles Map takes two
                questions and tells you which ones your business should hire.
              </p>
            </motion.div>

            {/* The quiz is the front door. Step one of it is the three role
                cards that used to sit below this asking the same question a
                second way. */}
            <div className="mt-5 mb-3">
              <ProblemFinder onRoleSelect={role => setActiveRoles([role])} />
            </div>

          </div>

          {/* Video: right column, desktop only */}
          <div className="hidden lg:flex w-full lg:w-[42%] items-center">
            <div
              style={{
                position: "relative",
                width: "100%",
                paddingBottom: "56.25%",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
              }}
            >
              <div
                ref={videoContainerRef}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default function Home() {

  const [activeRoles, setActiveRoles] = useState<Role[]>([]);
  const [activePricing, setActivePricing] = useState<PricingType[]>([]);
  const [activeSkill, setActiveSkill] = useState<SkillLevel[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleRole = (role: Role) => {
    setActiveRoles(prev =>
      prev.includes(role) ? [] : [role]
    );
  };

  const togglePricing = (p: PricingType) => {
    setActivePricing(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const toggleSkill = (s: SkillLevel) => {
    setActiveSkill(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const clearAll = () => {
    setActiveRoles([]);
    setActivePricing([]);
    setActiveSkill([]);
    setSearchQuery("");
  };

  const hasFilters = activeRoles.length > 0 || activePricing.length > 0 || activeSkill.length > 0 || searchQuery.length > 0;

  const filteredTools = useMemo(() => {
    return tools.filter(tool => {
      if (activeRoles.length > 0 && !activeRoles.some(r => tool.roles.includes(r))) return false;
      // A tool matches if it carries any selected tag, so filtering by "Free"
      // now surfaces everything with a usable free tier rather than only the
      // handful that were free and nothing else.
      if (activePricing.length > 0 && !activePricing.some(p => tool.pricingTypes.includes(p))) return false;
      if (activeSkill.length > 0 && !activeSkill.includes(tool.skillLevel)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !tool.name.toLowerCase().includes(q) &&
          !tool.description.toLowerCase().includes(q) &&
          !tool.useCases.some(u => u.toLowerCase().includes(q))
        ) return false;
      }
      return true;
    });
  }, [activeRoles, activePricing, activeSkill, searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* ── HERO ── */}
      <HeroSection setActiveRoles={setActiveRoles} />

      {/* The italic "Make sure to check out the resource button on each
          platform to guide you where to start" strip that sat here is gone,
          per the mockup. It explained a control before anyone had scrolled far
          enough to see a card that has one, and it sat between the quiz and
          the filters, which is where the eye is travelling fastest. */}

      {/* ── FILTER BAR ── */}
      <div className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-secondary border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-border" />

            {/* Role filters. These live here now that the hero's role cards
                have become step one of the quiz. Without them there would be
                no way to browse by role without answering questions. */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {ALL_ROLES.map(role => (
                <button
                  key={role}
                  onClick={() => toggleRole(role)}
                  className={`
                    inline-flex items-center min-h-11 px-4 text-sm font-medium rounded-full border transition-all duration-150
                    ${activeRoles.includes(role)
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground"
                    }
                  `}
                >
                  {ROLE_ICONS[role]} {ROLE_LABELS[role]}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-border" />

            {/* Pricing filters */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {PRICING_TYPES.map(p => (
                <button
                  key={p}
                  onClick={() => togglePricing(p)}
                  className={`
                    inline-flex items-center min-h-11 px-4 text-sm font-medium rounded-full border transition-all duration-150
                    ${activePricing.includes(p)
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground"
                    }
                  `}
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-border" />

            {/* Skill filters */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {SKILL_LEVELS.map(s => (
                <button
                  key={s}
                  onClick={() => toggleSkill(s)}
                  className={`
                    inline-flex items-center min-h-11 px-4 text-sm font-medium rounded-full border transition-all duration-150
                    ${activeSkill.includes(s)
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground"
                    }
                  `}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Clear */}
            {hasFilters && (
              <button
                onClick={clearAll}
                className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── TOOL GRID ── */}
      <main className="container py-10" id="tools-section">
        {/* Role context card */}
        {activeRoles.length === 1 && (
          <RoleContextCard role={activeRoles[0]} />
        )}
        {/* The directory had no heading at all: the page went from the filter
            bar straight to the count-and-filter line, so the largest section of
            the site was unnamed and the quiz appeared to be the whole page.
            The subhead gives the visitor explicit permission to ignore the
            quiz, which some will do anyway. */}
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 mb-6">
          <div>
            {/* No count in the heading. It was the only place on the page
                that had to be true about a number, and it is the kind of true
                that goes stale on the next commit that publishes or retires a
                tool. The filtered line below still counts, because there the
                number is the point. */}
            <h2 className="font-['Fraunces'] text-2xl font-bold text-foreground">
              All AI tools
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {hasFilters ? (
                <>
                  Showing{" "}
                  <span className="font-semibold text-foreground">
                    {filteredTools.length}
                  </span>{" "}
                  of {tools.length}.
                </>
              ) : (
                "Skip the quiz and browse everything."
              )}
            </p>
          </div>
        </div>

        {filteredTools.length === 0 ? (
          <div className="text-center py-16 px-6 bg-secondary/30 rounded-lg border border-border">
            <p className="font-['Fraunces'] text-3xl font-semibold text-foreground mb-3">No tools found</p>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Your current filters didn't match any tools. Try adjusting your search or filters to discover more.</p>
            <button 
              onClick={clearAll} 
              className={BUTTON.primarySm}
            >
              ✕ Clear all filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredTools.map(tool => (
                <div key={tool.id}>
                  <ToolCard tool={tool} />
                </div>
              ))}
          </div>
        )}
      </main>

      {/* ── RESOURCE CENTER CTA ── */}
      <section className="bg-secondary/30 border-t border-border mt-16 py-8">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-['Fraunces'] text-lg font-semibold text-foreground mb-1">
              Explore Resources
            </h3>
            <p className="text-sm text-muted-foreground">
              Video hosting, design tools, and services that work alongside AI.
            </p>
          </div>
          <Link
            href="/resources"
            className={`shrink-0 ${BUTTON.primarySm}`}
          >
            Explore the Resource Center
          </Link>
        </div>
      </section>

      {/* SiteFooter carries the back-to-top control now, so the homepage's own
          one is gone. The two were both fixed bottom-right and stacked — the
          site-wide one is the storefront's, sits at z-40 under the sticky
          header rather than z-50 over it, and hides with `visibility` so it is
          not a focusable target while invisible. */}
      <SiteFooter />
    </div>
  );
}

// ── TOOL CARD ──
function ToolCard({ tool }: { tool: (typeof tools)[0] }) {
  const primaryRole = tool.roles[0];
  const borderStyle = ROLE_BORDER_STYLES[primaryRole];
  const { isAuthenticated, loading } = useAuth();
  const { open } = useAuthModal();

  /**
   * Signed-out visitors get the dialog before the resource page.
   *
   * This stays a real <a href> rather than a button so the link is still in
   * the markup for crawlers, and the pages themselves stay publicly
   * reachable: the point of un-gating was that they can be indexed. What is
   * gated is the path through the UI: a person clicking here is asked to
   * create an account first, and `returnTo` puts them on the page they wanted
   * the moment they have one.
   *
   * While auth is still resolving the click is left alone, so a slow query
   * cannot make the link silently do nothing.
   */
  const handleResourceClick = (e: React.MouseEvent) => {
    if (loading || isAuthenticated || !tool.routeLink) return;
    e.preventDefault();
    open({
      mode: "signup",
      returnTo: tool.routeLink,
      title: `See how to start with ${tool.name}`,
      description:
        "Create a free account to open the resource guides. It takes a moment.",
    });
  };

  return (
    <div
      className={`
        group bg-card rounded-lg border border-border ${borderStyle}
        shadow-[0_2px_8px_oklch(0.22_0.015_65/0.06)]
        card-lift flex flex-col h-full
      `}
    >
      <div className="p-5 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <ToolLogo tool={tool} />
          {/* See PricingBadge for why this is one pill and not one per tier. */}
          <div className="shrink-0">
            <PricingBadge types={tool.pricingTypes} />
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-foreground/70 leading-relaxed mb-4 flex-1">
          {tool.description}
        </p>

        {/* Best for */}
        <p className="text-xs text-muted-foreground italic mb-4 border-t border-border pt-3">
          {tool.bestFor}
        </p>

        {/* Role badges */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tool.roles.map(role => (
            <span
              key={role}
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_BADGE_STYLES[role]}`}
            >
              {ROLE_ICONS[role]} {ROLE_LABELS[role]}
            </span>
          ))}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground border border-border">
            {tool.skillLevel}
          </span>
        </div>

        {/* One way off the card, not two.
            The outbound "Visit" link is gone at Daniel's request. It was the
            affiliate link, so note where that traffic goes now: every tool
            has a resource page, and every affiliate URL lives on that page.
            Nothing was lost: the affiliate click is one step deeper than it
            was. `tool.website` is still in the data and verify:links still
            guards every one of those URLs.

            The pricing sentence is gone too. It duplicated the Free / Paid
            tags at the top of the card, and half of them still opened with
            the word "Freemium", which the tags no longer use. */}
        <div className="flex items-center justify-between gap-2">
          {/* Adding a tool straight from the directory is a real path: some
              people already know what they want and should not have to answer
              two questions to keep it. */}
          <SaveToolButton toolId={tool.id} toolName={tool.name} />
          {tool.routeLink && (
            <a
              href={tool.routeLink}
              onClick={handleResourceClick}
              // One action colour (--primary), one label, one shape. This was
              // a hardcoded #0F6E56, which is the Manus role colour leaking
              // into the action layer, and the Resource Center used the same
              // hex at a different radius under a different name.
              // min-h-11 because the old 26px target was too small to hit
              // reliably on a phone.
              className={BUTTON.primarySm}
            >
              See how to start
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
